import type { TypedDocumentNode } from '@apollo/client'
import type * as GraphQLOperations from 'types/graphql'

import { useEffect, useRef, useState } from 'react'

import { gql } from '@apollo/client'

import { useQuery } from '@apollo/client/react'

import { useAuth } from 'src/auth'
import { useAgentChatSessions } from 'src/components/AnalyticsChat/AgentChatProvider'
import AgentSidebar from 'src/components/AnalyticsChat/ConversationsSidebar'
import { PortalWorkspaceEmptyState } from 'src/components/businessPortal/PortalWorkspaceEmptyState'
import type { PortalMode } from 'src/components/businessPortal/PortalModeSwitcher'
import {
  BookOpenIcon,
  BriefcaseBusinessIcon,
  ClipboardCheckIcon,
  CreditCardIcon,
  FileTextIcon,
  GavelIcon,
  HeartPulseIcon,
  HouseIcon,
  LayoutDashboardIcon,
  LayoutGridIcon,
  Icon,
  IconButton,
  PanelLeftIcon,
  ListChecksIcon,
  MessagesCircleIcon,
  UserIcon,
  WrenchIcon,
  UiProvider,
  useViewportQuery,
  type UiColorMode,
  type UiThemeName,
} from 'src/design-system/ui'
import {
  buildAgentConversationPath,
  buildNewAgentChatPath,
  getPersistedAgentConversationId,
} from 'src/lib/businessPortal/agentRoutes'
import { buildPermissionChecker } from 'src/lib/businessPortal/permissions'
import {
  canAccessAgent,
  canAccessMetrics,
  canAccessMonitor,
  isMetricsViewerChoice,
} from 'src/lib/businessPortal/portalSurfaceAccess'
import { PORTAL_PM_MANUAL_NOTICE_QUEUE_ENABLED } from 'src/lib/businessPortal/portalPmQueries'
import { usePortalManualEntryPm } from 'src/lib/businessPortal/usePortalManualEntryPm'
import { ProfessionalDocumentCanvas } from 'src/layouts/_shared/ProfessionalDocumentCanvas'
import { useProfessionalMode } from 'src/layouts/_shared/useProfessionalMode'
import { navigate, useLocation, useParams } from 'src/platform/router'

import {
  BusinessPortalProvider,
  type BusinessPortalContextValue,
  type BusinessPortalNavItem,
  type BusinessPortalWorkspaceAccess,
} from './useBusinessPortal'
import { BusinessPortalFrame } from './BusinessPortalFrame'
import { BusinessPortalNavigation } from './BusinessPortalNavigation'
import { BusinessPortalTopBar } from './BusinessPortalTopBar'
import { BusinessPortalWorkspaceControls } from './BusinessPortalWorkspaceControls'

// Owner-group dashboard access is not a role — it is resolved from
// AuthzAccessEntry by getAllowedMetricsChoices. The nav needs it to decide
// whether to show the Metrics tab for a viewer who holds no portal role.
export const GET_METRICS_CHOICES: TypedDocumentNode<
  GraphQLOperations.GetPortalMetricsChoices,
  GraphQLOperations.GetPortalMetricsChoicesVariables
> = gql`
  query GetPortalMetricsChoices {
    getAllowedMetricsChoices {
      choices {
        kind
        key
      }
    }
  }
`

export const GET_PM_NAMES: TypedDocumentNode<
  GraphQLOperations.GetBusinessPortalWorkspaces,
  GraphQLOperations.GetBusinessPortalWorkspacesVariables
> = gql`
  query GetBusinessPortalWorkspaces {
    businessPortalWorkspaces {
      pmName
      displayName
      permissions
      accessiblePropertyCodes
      trainingEnabled
      corporateScreeningsEnabled
      portfolioAssistantConfig {
        enabled
        strategistEnabled
      }
      agentConnectorsConfig {
        enabled
        graphqlToolsEnabled
        yardiReadEnabled
      }
    }
  }
`

// Per-browser flag: the Integration nav item shows a "New" indicator until the
// user has visited it once.
const INTEGRATION_SEEN_KEY = 'twodots.businessPortal.integrationSeen'

interface BusinessPortalLayoutProps {
  children: React.ReactNode
  appearance?: {
    theme: UiThemeName
    mode: UiColorMode
    chooseMode: (mode: UiColorMode) => void
  }
}

const encodePm = (pm: string) => encodeURIComponent(pm).replace(/\./g, '%2E')

const decodePm = (pm: string) => {
  try {
    return decodeURIComponent(pm)
  } catch {
    return pm
  }
}

const buildPoliciesPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/policies` : '/portal/policies'

const buildPolicyDetailPath = (pm: string, policyId: string) =>
  `/portal/${encodePm(pm)}/policies/policy/${encodeURIComponent(policyId)}`

const buildPropertiesPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/properties` : '/portal/properties'

const buildPricingsPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/pricings` : '/portal/pricings'

const buildPricingDetailPath = (pm: string, planGroupId: string) =>
  `/portal/${encodePm(pm)}/pricings/plan/${encodeURIComponent(planGroupId)}`

const buildDebugPath = (pm: string) => `/portal/${encodePm(pm)}/debug`

const buildPropertyDetailPath = (pm: string, propertyCode: string) =>
  `/portal/${encodePm(pm)}/properties/property/${encodeURIComponent(propertyCode)}`

const buildApplicationsPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/applications` : '/portal/applications'

const buildCorporateScreeningsPath = (pm?: string | null) =>
  pm
    ? `/portal/${encodePm(pm)}/corporate-screenings`
    : '/portal/corporate-screenings'

const buildCorporateScreeningDetailPath = (pm: string, corporationId: number) =>
  `/portal/${encodePm(pm)}/corporate-screenings/corporation/${corporationId}`

const buildMetricsPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/metrics` : '/portal/metrics'

const buildMonitorPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/monitor` : '/portal/rpm/monitor'

const buildAgentPath = (pm?: string | null) =>
  pm ? buildNewAgentChatPath(pm) : '/portal/agent'

const buildAgentActivityPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/agent/activity` : '/portal/agent/activity'

const buildTrainingPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/training` : '/portal/training'

const buildNoticesPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/notices` : '/portal/notices'

const buildTeamAccessPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/team-access` : '/portal/team-access'

const buildRentcafeSetupPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/rentcafe` : '/portal/rentcafe'

const buildIntegrationPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/integration` : '/portal/integration'

const buildIntegrationRentcafePath = (pm?: string | null) =>
  pm
    ? `/portal/${encodePm(pm)}/integration/rentcafe`
    : '/portal/integration/rentcafe'

const buildCompliancePath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/compliance` : '/portal/compliance'

const buildLegalPath = (pm?: string | null) =>
  pm ? `/portal/${encodePm(pm)}/legal` : '/portal/legal'

export const buildPortalSurfacePath = ({
  fallback,
  pathname,
  pm,
  preserveTraining = true,
}: {
  fallback: string
  pathname: string
  pm: string | null
  preserveTraining?: boolean
}) => {
  if (pathname.includes('/properties')) return buildPropertiesPath(pm)
  if (pathname.includes('/pricings')) return buildPricingsPath(pm)
  if (pathname.includes('/applications')) return buildApplicationsPath(pm)
  if (pathname.includes('/corporate-screenings'))
    return buildCorporateScreeningsPath(pm)
  if (pathname.includes('/metrics')) return buildMetricsPath(pm)
  if (pathname.includes('/agent/activity')) return buildAgentActivityPath(pm)
  if (pathname.includes('/agent')) return buildAgentPath(pm)
  if (preserveTraining && pathname.includes('/training'))
    return buildTrainingPath(pm)
  if (pathname.includes('/notices')) return buildNoticesPath(pm)
  if (pathname.includes('/team-access')) return buildTeamAccessPath(pm)
  if (pathname.includes('/integration')) return buildIntegrationPath(pm)
  if (pathname.includes('/rentcafe')) return buildRentcafeSetupPath(pm)
  if (pathname.includes('/compliance')) return buildCompliancePath(pm)
  if (pathname.includes('/legal')) return buildLegalPath(pm)
  return fallback
}

/**
 * Where a workspace lands when the URL names no particular tab: Metrics is
 * the portal's homepage for everyone whose grant (or internal role) opens
 * it; everyone else gets the first tab their permissions actually open.
 *
 * Explicit destinations are unaffected — onboarding wizards and deep links
 * name their own tab and never pass through this fallback.
 */
export const buildDefaultLandingPath = (
  workspace: BusinessPortalWorkspaceAccess | null,
  isInternalAdmin = false
) => {
  const pmName = workspace?.pmName ?? null
  if (!workspace) {
    return isInternalAdmin
      ? buildMetricsPath(pmName)
      : buildPoliciesPath(pmName)
  }

  const access = buildPermissionChecker(workspace.permissions)
  if (isInternalAdmin || access.can('metrics', 'read')) {
    return buildMetricsPath(pmName)
  }
  if (access.can('policies', 'read')) return buildPoliciesPath(pmName)
  if (access.can('properties', 'read')) return buildPropertiesPath(pmName)
  if (access.can('leasingAgent', 'read')) return buildApplicationsPath(pmName)
  if (access.can('corporateScreenings', 'read'))
    return buildCorporateScreeningsPath(pmName)
  if (access.can('pricing', 'read')) return buildPricingsPath(pmName)
  if (access.can('notices', 'read')) return buildNoticesPath(pmName)
  if (access.can('compliance', 'read')) return buildCompliancePath(pmName)
  if (access.can('integration', 'read')) return buildIntegrationPath(pmName)
  if (access.can('teamSettings', 'read')) return buildTeamAccessPath(pmName)
  if (access.can('legal', 'read')) return buildLegalPath(pmName)

  // No readable tab at all — land on Policies and let the page render its own
  // empty state rather than bouncing the user around.
  return buildPoliciesPath(pmName)
}

const BusinessPortalLayout = ({
  children,
  appearance: appearanceOverride,
}: BusinessPortalLayoutProps) => {
  const agentSessions = useAgentChatSessions()
  const savedAppearance = useProfessionalMode()
  const appearance = appearanceOverride ?? {
    ...savedAppearance,
    theme: 'professional' as const,
  }
  const { hasRole } = useAuth()
  const { pathname } = useLocation()
  const { conversationId, pm } = useParams()
  const currentPm = pm ? decodePm(pm) : null
  const isAgentRoute = pathname.includes('/agent')
  const isAgentActivityRoute = pathname.includes('/agent/activity')
  const isDesktop = useViewportQuery('(min-width: 900px)')
  const [desktopExpanded, setDesktopExpanded] = useState(true)
  const [agentNavigationExpanded, setAgentNavigationExpanded] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const lastPortalPathByPmRef = useRef(new Map<string, string>())

  useEffect(() => {
    window.Intercom('update', { hide_default_launcher: true })
    return () => {
      window.Intercom('update', { hide_default_launcher: false })
    }
  }, [])

  const [integrationSeen, setIntegrationSeen] = useState(() => {
    if (typeof window === 'undefined') return true
    try {
      return localStorage.getItem(INTEGRATION_SEEN_KEY) === 'true'
    } catch {
      return true
    }
  })

  const { data: namesData, loading: pmNamesLoading } = useQuery(GET_PM_NAMES)
  // Internal non-Admin callers get a Forbidden from this resolver; that is a
  // "no owner dashboards" answer for nav purposes, not a failure worth
  // surfacing.
  const { data: metricsChoicesData } = useQuery(GET_METRICS_CHOICES, {})
  const hasMetricsChoices = (
    (metricsChoicesData?.getAllowedMetricsChoices?.choices ?? []) as {
      kind: string
    }[]
  ).some(isMetricsViewerChoice)
  const workspaces = (namesData?.businessPortalWorkspaces ??
    []) as BusinessPortalWorkspaceAccess[]
  const pmNames = workspaces.map((workspace) => workspace.pmName)
  const isInternalAdmin = hasRole('Admin') || hasRole('Super')
  const hasFullPortalAdmin = hasRole('Admin')
  // Internal admins can switch across every PM; external users get the picker
  // only when they belong to more than one workspace.
  const canSelectWorkspace = isInternalAdmin || workspaces.length > 1
  const firstWorkspace = workspaces[0]?.pmName ?? null
  const currentPmIsAllowed = currentPm ? pmNames.includes(currentPm) : false
  const currentWorkspace =
    workspaces.find((workspace) => workspace.pmName === currentPm) ?? null
  const firstWorkspaceLanding = buildDefaultLandingPath(
    workspaces[0] ?? null,
    isInternalAdmin
  )
  // Effective permissions on the selected workspace, unioned across every role
  // the viewer holds there. Nav visibility reads this; the resolvers enforce
  // the identical grant server-side.
  const access = buildPermissionChecker(currentWorkspace?.permissions)
  const { data: noticesAvailability } = useQuery(
    PORTAL_PM_MANUAL_NOTICE_QUEUE_ENABLED,
    {
      variables: { pmName: currentPm ?? '' },
      skip: !currentPm || !access.can('notices', 'read'),
    }
  )
  const noticesEnabled =
    access.can('notices', 'read') &&
    noticesAvailability?.portalPmManualNoticeQueueEnabled === true
  // Composite tab rules live in portalSurfaceAccess, shared with the pages
  // themselves, so the nav can never show a tab whose page bounces or hide
  // one that works.
  const canSeeMetricsTab = canAccessMetrics({
    hasRole,
    can: access.can,
    hasMetricsChoices,
  })
  const canSeeMonitorTab = canAccessMonitor({
    hasRole,
    can: access.can,
    currentPm,
  })
  const canSeeAgentTab = canAccessAgent({
    agentEnabled: currentWorkspace?.portfolioAssistantConfig.enabled ?? false,
    hasRole,
    can: access.can,
  })
  const isAgentWorkspace = isAgentRoute && canSeeAgentTab
  const { hasPms } = usePortalManualEntryPm(
    currentPm,
    access.can('compliance', 'read')
  )
  // Training is rolled out per PM — only show its tab for a workspace that's
  // live on the feature (see TRAINING_ENABLED_PMS on the api).
  const trainingEnabled = currentWorkspace?.trainingEnabled ?? false
  // Two Dots enables corporate screening per PM; until then the tab does not
  // exist for anyone, whatever their permissions.
  const corporateScreeningsEnabled =
    currentWorkspace?.corporateScreeningsEnabled ?? false

  useEffect(() => {
    setMobileOpen(false)
  }, [isDesktop, pathname])

  useEffect(() => {
    if (isAgentWorkspace) setAgentNavigationExpanded(true)
  }, [isAgentWorkspace])

  useEffect(() => {
    if (!isAgentRoute && currentPm) {
      lastPortalPathByPmRef.current.set(currentPm, pathname)
    }
  }, [currentPm, isAgentRoute, pathname])

  // Visiting Integration (by any means) retires its "New" indicator.
  useEffect(() => {
    if (!pathname.includes('/integration')) return
    setIntegrationSeen(true)
    try {
      localStorage.setItem(INTEGRATION_SEEN_KEY, 'true')
    } catch {
      // localStorage unavailable (private mode) — indicator just won't persist.
    }
  }, [pathname])

  useEffect(() => {
    if (pmNamesLoading || isInternalAdmin || !firstWorkspace) return
    if (currentPm && currentPmIsAllowed) return

    navigate(
      buildPortalSurfacePath({
        fallback: firstWorkspaceLanding,
        pathname,
        pm: firstWorkspace,
        preserveTraining: false,
      })
    )
  }, [
    isInternalAdmin,
    currentPm,
    currentPmIsAllowed,
    firstWorkspace,
    firstWorkspaceLanding,
    pathname,
    pmNamesLoading,
  ])

  const handleMenuClick = () => {
    if (isDesktop) {
      if (isAgentWorkspace) {
        setAgentNavigationExpanded((isExpanded) => !isExpanded)
        return
      }
      setDesktopExpanded((isExpanded) => !isExpanded)
      return
    }

    setMobileOpen((isOpen) => !isOpen)
  }

  const handleNavClick = (href: string) => {
    navigate(href)

    if (!isDesktop) {
      setMobileOpen(false)
    }
  }

  const handleModeChange = (mode: PortalMode) => {
    if (mode === 'agent') {
      setAgentNavigationExpanded(true)
      const conversationId = agentSessions?.returnConversationId(currentPm)
      handleNavClick(
        currentPm && conversationId
          ? buildAgentConversationPath(currentPm, conversationId)
          : buildAgentPath(currentPm)
      )
      return
    }

    const returnPath = currentPm
      ? lastPortalPathByPmRef.current.get(currentPm)
      : null
    handleNavClick(
      returnPath ?? buildDefaultLandingPath(currentWorkspace, isInternalAdmin)
    )
  }

  const handleAgentChatOpen = () => {
    agentSessions?.stopWorkspace(currentPm)
    handleNavClick(buildAgentPath(currentPm))
  }

  const handleAgentConversationOpen = (id: string) => {
    if (currentPm) handleNavClick(buildAgentConversationPath(currentPm, id))
  }

  const handleAgentActivityOpen = () => {
    handleNavClick(buildAgentActivityPath(currentPm))
  }

  const handlePmChange = (nextPm: string | null) => {
    const nextWorkspace =
      workspaces.find((workspace) => workspace.pmName === nextPm) ?? null
    handleNavClick(
      buildPortalSurfacePath({
        fallback: buildDefaultLandingPath(nextWorkspace, isInternalAdmin),
        pathname,
        pm: nextPm,
      })
    )
  }

  const workspaceNavItems: BusinessPortalNavItem[] = [
    // Metrics leads: it is the portal's homepage (buildDefaultLandingPath).
    ...(canSeeMetricsTab
      ? [
          {
            id: 'metrics',
            label: 'Metrics',
            icon: LayoutDashboardIcon,
            href: buildMetricsPath(currentPm),
            isActive: pathname.includes('/metrics'),
            isDisabled: false,
          },
        ]
      : []),
    {
      id: 'applications',
      label: 'Applications',
      icon: LayoutGridIcon,
      href: buildApplicationsPath(currentPm),
      isActive: pathname.includes('/applications'),
      isDisabled: !access.can('leasingAgent', 'read'),
    },
    ...(corporateScreeningsEnabled
      ? [
          {
            id: 'corporate-screenings',
            label: 'Corporate Screening',
            icon: BriefcaseBusinessIcon,
            href: buildCorporateScreeningsPath(currentPm),
            isActive: pathname.includes('/corporate-screenings'),
            isDisabled: !access.can('corporateScreenings', 'read'),
          },
        ]
      : []),
    {
      id: 'policies',
      label: 'Policies',
      icon: FileTextIcon,
      href: buildPoliciesPath(currentPm),
      isActive: pathname.includes('/policies'),
      isDisabled: !access.can('policies', 'read'),
    },
    {
      id: 'properties',
      label: 'Properties',
      icon: HouseIcon,
      href: buildPropertiesPath(currentPm),
      isActive: pathname.includes('/properties'),
      isDisabled: !access.can('properties', 'read'),
    },
    {
      id: 'pricings',
      label: 'Pricing',
      icon: CreditCardIcon,
      href: buildPricingsPath(currentPm),
      isActive: pathname.includes('/pricings'),
      isDisabled: !access.can('pricing', 'read'),
    },
    ...(canSeeMonitorTab
      ? [
          {
            id: 'monitor',
            label: 'Centralized Monitor',
            icon: HeartPulseIcon,
            href: buildMonitorPath(currentPm),
            isActive: pathname.includes('/monitor'),
            isDisabled: false,
          },
        ]
      : []),
    // Training appears for workspaces live on the feature. Two Dots staff see
    // it for EVERY workspace, so they can open the rollout panel and turn
    // training on (the page itself shows only the staff panel until enabled).
    ...(trainingEnabled || canSelectWorkspace
      ? [
          {
            id: 'training',
            label: 'Training',
            icon: BookOpenIcon,
            href: buildTrainingPath(currentPm),
            isActive: pathname.includes('/training'),
            isDisabled: false,
          },
        ]
      : []),
    ...(noticesEnabled
      ? [
          {
            id: 'notices',
            label: 'Notices',
            icon: MessagesCircleIcon,
            href: buildNoticesPath(currentPm),
            isActive: pathname.includes('/notices'),
            isDisabled: false,
          },
        ]
      : []),
    {
      id: 'team-access',
      label: 'Team Settings',
      icon: UserIcon,
      href: buildTeamAccessPath(currentPm),
      isActive: pathname.includes('/team-access'),
      isDisabled: !access.can('teamSettings', 'read'),
    },
    {
      id: 'integration',
      label: 'Integration',
      icon: ListChecksIcon,
      href: buildIntegrationPath(currentPm),
      isActive: pathname.includes('/integration'),
      isDisabled: !access.can('integration', 'read'),
      isNew: access.can('integration', 'write') && !integrationSeen,
    },
    ...(hasPms
      ? [
          {
            id: 'compliance',
            label: 'Compliance',
            icon: ClipboardCheckIcon,
            href: buildCompliancePath(currentPm),
            isActive: pathname.includes('/compliance'),
            isDisabled: !access.can('compliance', 'read'),
          },
        ]
      : []),
    ...(access.can('legal', 'read') || (isInternalAdmin && !currentPm)
      ? [
          {
            id: 'legal',
            label: 'Legal',
            icon: GavelIcon,
            href: buildLegalPath(currentPm),
            isActive: pathname.includes('/legal'),
            isDisabled: false,
          },
        ]
      : []),
  ]

  const navItems = currentPm
    ? workspaceNavItems
    : workspaceNavItems.map((item) => ({
        ...item,
        isDisabled: true,
        isActive: false,
      }))

  // Owner-only dashboard accounts use the metrics shell to resolve their
  // granted list; keep that existing redirect mounted without a PM workspace.
  const resolvingMetricsDashboard =
    pathname === '/portal/metrics' && !isInternalAdmin && pmNames.length === 0

  // Internal-admin-only entry into the debug / hand-over tools, shown in the
  // Workspace section beside the PM picker; needs a PM selected.
  const debugNavItem: BusinessPortalNavItem | null =
    hasFullPortalAdmin && currentPm
      ? {
          id: 'debug',
          label: 'Debug',
          icon: WrenchIcon,
          href: buildDebugPath(currentPm),
          isActive:
            pathname.includes('/debug') && !pathname.includes('/policies'),
          isDisabled: false,
        }
      : null

  // Fall back to the first tab the viewer can actually open, not whatever
  // happens to be first in the list.
  const currentNavItem =
    !currentPm || isAgentRoute
      ? null
      : (navItems.find((navItem) => navItem.isActive) ??
        navItems.find((navItem) => !navItem.isDisabled) ??
        navItems[0] ??
        null)

  const contextValue: BusinessPortalContextValue = {
    currentPm,
    currentWorkspace,
    workspaceAccessLoading: pmNamesLoading,
    isInternalAdmin,
    canSelectWorkspace,
    pmNames,
    pmNamesLoading,
    trainingEnabled,
    can: access.can,
    canAny: access.canAny,
    canAll: access.canAll,
    hasPermission: access.has,
    currentNavItem,
    navItems,
    policiesPath: (pmOverride) => buildPoliciesPath(pmOverride ?? currentPm),
    policyDetailPath: (policyId) =>
      currentPm ? buildPolicyDetailPath(currentPm, policyId) : null,
    propertiesPath: (pmOverride) =>
      buildPropertiesPath(pmOverride ?? currentPm),
    propertyDetailPath: (propertyCode) =>
      currentPm ? buildPropertyDetailPath(currentPm, propertyCode) : null,
    pricingsPath: (pmOverride) => buildPricingsPath(pmOverride ?? currentPm),
    pricingDetailPath: (planGroupId) =>
      currentPm ? buildPricingDetailPath(currentPm, planGroupId) : null,
    applicationsPath: (pmOverride) =>
      buildApplicationsPath(pmOverride ?? currentPm),
    corporateScreeningsPath: (pmOverride) =>
      buildCorporateScreeningsPath(pmOverride ?? currentPm),
    corporateScreeningDetailPath: (corporationId) =>
      currentPm
        ? buildCorporateScreeningDetailPath(currentPm, corporationId)
        : null,
    noticesPath: (pmOverride) => buildNoticesPath(pmOverride ?? currentPm),
    teamAccessPath: (pmOverride) =>
      buildTeamAccessPath(pmOverride ?? currentPm),
    integrationPath: (pmOverride) =>
      buildIntegrationPath(pmOverride ?? currentPm),
    integrationRentcafePath: () =>
      currentPm ? buildIntegrationRentcafePath(currentPm) : null,
    compliancePath: (pmOverride) =>
      buildCompliancePath(pmOverride ?? currentPm),
    setCurrentPm: handlePmChange,
  }

  const workspaceControls =
    canSelectWorkspace || debugNavItem ? (
      <BusinessPortalWorkspaceControls
        canSelectWorkspace={canSelectWorkspace}
        currentPm={currentPm}
        debugNavItem={debugNavItem}
        onDebugClick={handleNavClick}
        onPmChange={handlePmChange}
        pmNamesLoading={pmNamesLoading}
        workspaces={workspaces}
      />
    ) : undefined
  const navigationContent = isAgentWorkspace ? (
    <AgentSidebar
      activitySelected={isAgentActivityRoute}
      expanded={!isDesktop || agentNavigationExpanded}
      onModeChange={handleModeChange}
      onNewChat={handleAgentChatOpen}
      onOpenActivity={handleAgentActivityOpen}
      onSelectConversation={handleAgentConversationOpen}
      pm={currentPm}
      selectedId={
        isAgentActivityRoute
          ? null
          : getPersistedAgentConversationId(conversationId)
      }
      workspaceControls={workspaceControls}
    />
  ) : (
    <BusinessPortalNavigation
      agentModeAvailable={canSeeAgentTab}
      canSelectWorkspace={canSelectWorkspace}
      currentPm={currentPm}
      debugNavItem={debugNavItem}
      expanded={!isDesktop || desktopExpanded}
      navItems={navItems}
      onModeChange={handleModeChange}
      onNavClick={handleNavClick}
      onPmChange={handlePmChange}
      pmNamesLoading={pmNamesLoading}
      workspaces={workspaces}
    />
  )

  return (
    <UiProvider
      {...(appearance.theme === 'evie'
        ? { theme: 'evie', mode: 'light' }
        : { theme: 'professional', mode: appearance.mode })}
    >
      <ProfessionalDocumentCanvas />
      <BusinessPortalProvider value={contextValue}>
        <BusinessPortalFrame
          expanded={
            isAgentWorkspace ? agentNavigationExpanded : desktopExpanded
          }
          isAgentWorkspace={isAgentWorkspace}
          isAgentActivityRoute={isAgentActivityRoute}
          isDesktop={isDesktop}
          mobileOpen={mobileOpen}
          navigation={navigationContent}
          onMobileOpenChange={setMobileOpen}
          topBar={
            <BusinessPortalTopBar
              {...appearance}
              leadingContent={
                <IconButton
                  aria-label={
                    isAgentWorkspace
                      ? 'toggle AI Agent navigation'
                      : 'toggle portal navigation'
                  }
                  aria-expanded={
                    isDesktop
                      ? isAgentWorkspace
                        ? agentNavigationExpanded
                        : desktopExpanded
                      : mobileOpen
                  }
                  onPress={handleMenuClick}
                >
                  <Icon icon={PanelLeftIcon} tone="current" />
                </IconButton>
              }
            />
          }
        >
          {!currentPm && !resolvingMetricsDashboard ? (
            <PortalWorkspaceEmptyState
              onChooseWorkspace={
                !isDesktop ? () => setMobileOpen(true) : undefined
              }
            />
          ) : (
            children
          )}
        </BusinessPortalFrame>
      </BusinessPortalProvider>
    </UiProvider>
  )
}

export default BusinessPortalLayout
